import { gridMap } from '../mapgrid.ts';
import { withGeometry } from '../mapgeo.ts';
import { PLAN_LETTERS, plazaGeometry } from './plazageo.ts';

/**
 * Plaza: a downtown block round a civic square with a fountain, zone B on its dry splash pad. Zone A is the metro plaza at the
 * Civic Centre station (C its twin at Market Street). Round it: a parking garage, a tower going up behind hoarding, the Meridian
 * Tower lobby, a cafe with a terrace, the public library, a pharmacy and a late-night mart, a pocket park, food trucks, the bus
 * terminal the red side spawns on, and a row of shops on the south avenue with a service alley behind. The east half is the
 * half turn of this one, dressed as other places by the theme (src/client/themes/plaza*.ts).
 *
 * The west half is drawn below. Building letters (plazageo.ts PLAN_KEY) become polygons: W facade wall, G shop glass, K counter or
 * desk, S shelving, P planter, H hoarding, O column, Q pallet stack, E core or oven, Y scaffold, C site cabin, M metro stairwell,
 * N newsstand, L hedge, I block courses. Everything else is the grid legend (mapgrid.ts): crates, barrels, props, spawns, zone A.
 */
const PLAN = `
  WWWWWWWWWWWWWWWWWWWWWWWW..H.............................H...
  WEEE...................W..H.............................H...
  WEEE.........b.........W..H......................CCCCCC.H...
  WEEE.................g.W..H....O....O.EEEO....O..CCCCCC.H...
  WEEE...................W..H...........EEE........CCCCCC.H...
  WEEE....O.....O....O...W..H...........EEE...............H...
  WEEE...................W..H...........EEE...........e...H...
  WEEE.......bb..........W..H.....................p.......H...
  WEEE...........................O....O....O....O.........H.PP
  WEEE.........................c.......................QQ.H.PP
  WEEE....O.....O....O.................................QQ.H...
  WEEE...........................IIIII......IIIII......QQ.H...
  W......................W..H.............................H...
  W......................W..H...YYYYYYYY...YYYYYYY..c.....H...
  W......................W..H.................................
  W.c...........O....O...W..H.....................FFF.........
  W.....................bW..H.QQ.............i....FFF.........
  W.........KK...........W..H.QQ..............QQQ.FFF.........
  W.....o...KK........c..W..H.QQ..............QQQ.........H...
  W......................W..H...........bbb..............bH...
  WWWW....WWWWWWW...WWWWWW..H............................bH...
  ..........................HHHHHHHH....HHHHHHHHHHH....HHHH...
  ............................................................
  ..........................FFF...............................
  ..........................FFF...............................
  ..........................FFF.......................c.......
  ............................................................
  ...............l............................PP...l..........
  WWWWWWWW...WWWWWWWWG........................................
  W..................G......NN...........MMMMM................
  W................P.G......NN....A......MMMMM..GWWWWWWWWWW...
  W..................G...................MMMMM..G.........W...
  W..EEEEE....KKKK...G...................MMMMM..G.........W...
  W..EEEEE.......K...G..........................G..KKKKK..W...
  W..EEEEE.......K.....................................K..W...
  W..EEEEE.............................................K..W...
  W..EEEEE......................................G.........W...
  W..EEEEE...K.......G..........................G.........W...
  W..EEEEE...........G..........................G........SW...
  W..EEEEE...K.......G..........................G........SW...
  W..EEEEE...........G..........................G........SW...
  W..EEEEE...K.......G.........PPP...PPP........GGGG..GGGGG...
  W..................G........................................
  W..........K.......G........................................
  W.P......a.......P.G..............c.......................PP
  W..................G......................................PP
  WWWWWW...WWWWWWWWWWG..........................PPPP...PPPP...
  ............................................................
  ............................................................
  .............................WWWWWW...WWWWWW...........PP...
  .............................W.............W................
  .............................W.............W................
  ......XXXXX..................W..SSS...SSS..W...PP...........
  ......XXXXX..................W.............W...PP...........
  ......XXXXX................................W................
  ......XXXXX.................................................
  ......XXXXX.................................................
  ......XXXXX............PPP...W..............................
  ......XXXXX............PPP...W.............W................
  ......XXXXX..................W..SSS...SSS..W................
  ......XXXXX..................W.............W................
  ......XXXXX..................W.....KKK.....W................
  ......XXXXX..................WWWWWWWWWWWWWWW................
  ......XXXXX.................................................
  ......XXXXX...........................................b.....
  ......XXXXX.................................................
  .............................WWW..WWWG.......c.PP...........
  .............................W.......G.........PP...........
  .............................W.K..m..G......................
  .................c...........W.K............................
  .............c...............W.........................PP...
  .............................W.SSSSS.G......................
  .............................W.......G......................
  .............................WWWWWWWWG......................
  .LLLLLL....LLLLLL...........................................
  ................L...........................................
  ................L.........c.................................
  ................L...........GGGGG...GGGGGGG.................
  .FFF............L...........W.............W.................
  .FFF............L...........W...........KKW.................
  .FFF........................W..SSSSSSSS...W...........FFFF..
  ............................W.............W...........FFFF..
  ..........................................W...........FFFF..
  .......................PPP.....SSSSSSSS...W.................
  .......................PPP..W.............W.................
  ................L...........W...............................
  ................L...........W..SSSSSSSS.....................
  ................L...........W.............W.................
  .............i..L...........W.............W....p..........b.
  ..............bbL...........WWWWWWWWWWWWWWW...............b.
  ................L...........................................
  .LLLLLL....LLLLLL...........................................
  .........................................b..c...............
  ........................................b...................
  ............................................................
  ...........................l................................
  ..c...c.....................................................
  ............................................................
  ............................................................
  GGGGG..GGGGGG..GGGGG......GGGG...GGGGGGG..GGGGGGG...GGGGG...
  W.........W........W......W.........W.........W.........W...
  W.........W........W......W.........W.........W.........W...
  W.........W........W......W.S..S..S.W.S.....S.W.KK...KK.W...
  W.KKKKKK..W.P....P.W......W.S..S..S.W.S.....S.W.KK...KK.W...
  W.........W.P....P.W......W.S..S..S.W.S.....S.W.........W...
  W.........W.P....P.W......W.S..S..S.W.S.....S.W.........W...
  W.........W.P....P.W......W.S..S..S.W.S.....S.W.KK...KK.W...
  W.........W.P....P.W......W.S..S..S.W.S.....S.W.KK...KK.W...
  W.........W.P....P.W......W.S..S..S.W.S.....S.W.........W...
  W.........W........W......W.........W...KKK...W.........W...
  W.EEEEEEE.W..KKKK..W......W.........W.........W...EEE...W...
  W.EEEEEEE.W........W......W.........W.........W...EEE...W...
  W.........W........W......W.........W.........W.........W...
  WWW..WWWWWWWWWWW..WW......WWWWWWW..WWW..WWWWWWWWWWWWW..WW...
  ..............b................c............................
  ............................................................
  ..........................................c.....FFFF........
  ........g..bb.........o..............bb.........FFFF........
  ................................................FFFF........
  ............................................................
`;

export const PLAZA = withGeometry(gridMap('Plaza', PLAN.replace(PLAN_LETTERS, '.'), 'plaza'), plazaGeometry(PLAN));
